import { useEffect, useState } from "react"

import { useGoogleLogin, useGoogleOAuth } from "@react-oauth/google"
import { Avatar, Card, Carousel, Flex, Layout, Spin, Typography } from "antd"
import { useTranslation } from "react-i18next"
import { connect } from "react-redux"
import { useParams } from "react-router"
import { useLocation, useNavigate } from "react-router-dom"

import ButtonComponent from "../Components/Button"
import AddEmailForm from "../Components/Forms/AddEmailForm"
import SignUpEmailForm from "../Components/Forms/SignUpEmailForm"
//import ShopifyLoginBtn from '../Components/ShopifyLoginBtn'
import PrivacyPolicy from "../Components/PrivacyPolicy"
import { RoutesPaths } from "../constants/user-routes"
import Images from "../Images"
import { defaultInnerPage } from "../Utils/appRoutes"
import { onboardingResumePath } from "../Utils/onboarding/productChoice"
import { getCookie, useQuery } from "../Utils/utils"
import { authErrorReset } from "./LoginPage/redux/auth.actions"
import { createAccount, socialAuth } from "./LoginPage/redux/auth.thunks"
import { getInviteUserInfo } from "./Setting/redux/setting.thunks"

import "./SignUpPage.less"

const { Text } = Typography

// Marketing carousel slides. Images live in public/signup/; copy is i18n-keyed.
const signUpSlides = [
  {
    img: "slide-1.png",
    title: "Avoid dispute monitoring programs",
    subhead:
      "Instantly receives pre-dispute signals identifying risky transactions before chargebacks occur."
  },
  {
    img: "slide-2.png",
    title: "Prevent chargebacks on autopilot",
    subhead:
      "The system allows you to stop a chargeback with a single click or automatically before it is even filed."
  },
  {
    img: "slide-3.png",
    title: "Protect your business with Chargeback",
    subhead:
      "The system automatically synchronizes transactions and is ready to work from the very first minute."
  }
]

function SignUpPage({
  isMobile,
  theme,
  auth: { error, fetching, userInfo, isAdmin },
  inviteInfoFetching,
  inviteInfo,
  isReferralOutsider,
  ...props
}) {
  const { search, state } = useLocation()
  const query = useQuery(search)
  const invite = query.get("invite")
  const source = query.get("source") || getCookie("user_source")
  const campaign = query.get("campaign") || getCookie("user_campaign")
  const referral_promo_code = query.get("promo-code")

  const navigate = useNavigate()
  const { hash } = useParams()
  const { t } = useTranslation()
  const { scriptLoadedSuccessfully } = useGoogleOAuth()

  const [acceptPolicyAgreement, setAcceptPolicyAgreement] = useState(true)

  const initialValues =
    !!userInfo && state === "change_email"
      ? { first_name: userInfo.first_name, last_name: userInfo.last_name, email: userInfo.email }
      : null

  function handleSubmit(data) {
    props.createAccount(
      {
        ...data,
        ...(invite && !!inviteInfo && { invite }),
        ...(source && campaign && { source, campaign }),
        ...(referral_promo_code && { referral_promo_code })
      },
      null,
      isReferralOutsider
    )
  }

  const responseGoogle = useGoogleLogin({
    onSuccess: (response) => {
      if (response && response?.access_token) {
        props.socialAuth(response?.access_token, "google", null, isReferralOutsider)
      }
    },
    onFailure: (response) => {
      if (response && response?.access_token) {
        props.socialAuth(response?.access_token, "google", null, isReferralOutsider)
      }
    }
  })

  useEffect(() => {
    if (hash) localStorage.setItem("ref_link_info", hash)
    if (invite) props.getInviteUserInfo(invite)
    if (userInfo && !isAdmin) {
      if (
        (userInfo?.new_onboarding_finished || userInfo?.onboarding_finished) &&
        userInfo?.["email_added"]
      ) {
        const defaultRoute = defaultInnerPage
        navigate(defaultRoute + (search || ""))
      } else {
        if (userInfo?.["email_added"]) {
          if (userInfo?.["is_email_verified"]) {
            navigate(onboardingResumePath(userInfo) ?? "/onboarding/business-info")
          } else {
            if (initialValues) return
            navigate("/getting_started")
          }
        } else {
          navigate("/add-email")
        }
      }
    } else if (userInfo && isAdmin) {
      navigate(RoutesPaths.alerts.all)
    } else {
      document.title = "Sign Up - Chargeback"
    }
    /*eslint-disable-next-line react-hooks/exhaustive-deps*/
  }, [])

  useEffect(() => {
    if (error) props.resetError()
    /*eslint-disable-next-line react-hooks/exhaustive-deps*/
  }, [error])

  /*const handleAuthShopify = (shopifyShop) => {
    props.shopifyAuth(shopifyShop)
  }*/

  return (
    <Layout>
      <Spin size="large" spinning={fetching || inviteInfoFetching}>
        <div className="signup-split">
          <div className="signup-split_left">
            <img
              className="signup-split_logo"
              src={Images?.[theme === "dark" ? "logoWithTextWhite" : "logoWithText"]}
              alt="Chargeback"
            />
            <div className="signup-split_body">
              {!userInfo?.email_added && Boolean(userInfo) ? (
                <Card className="login-card" style={{ minHeight: "208px" }}>
                  <h2 className="card-title">{t("Enter Your Email")}</h2>
                  <AddEmailForm onSubmit={handleSubmit} error={error?.message} />
                </Card>
              ) : (
                <>
                  <Card className="login-card">
                    {invite ? (
                      <Flex vertical align="center">
                        <Avatar
                          className="user_avatar"
                          size={40}
                          style={{
                            backgroundColor: inviteInfo?.business_account?.photo
                              ? "transparent"
                              : "#225AEA",
                            borderRadius: 12,
                            border: "none",
                            fontSize: 14,
                            minWidth: 40,
                            marginBottom: 18
                          }}
                          {...(inviteInfo?.business_account?.photo && {
                            src: inviteInfo?.business_account?.photo
                          })}
                        >
                          {!inviteInfo?.business_account?.photo
                            ? inviteInfo?.business_account?.name?.[0]
                            : null}
                        </Avatar>
                        <h2 className="card-title" style={{ alignSelf: "center" }}>
                          {t("Hey, join your team on Chargeback")}
                        </h2>
                        <h4
                          className="card-subtitle-signup"
                          style={{ textAlign: "center" }}
                        >{`You’ve been invited to use Chargeback, in a workspace called ${inviteInfo?.business_account?.name}.`}</h4>
                      </Flex>
                    ) : (
                      <>
                        <h2 className="card-title">
                          {isReferralOutsider
                            ? "Create your referral account"
                            : t("Create an account")}
                        </h2>
                        <h4 className="card-subtitle-signup">
                          {isReferralOutsider
                            ? "Let’s get your Referral account ready"
                            : t("Let’s set up your Chargeback account.")}
                        </h4>
                      </>
                    )}
                    <SignUpEmailForm
                      {...(initialValues && { initialValues })}
                      {...(invite && inviteInfo && { inviteInfo })}
                      onSubmit={handleSubmit}
                      error={error?.message}
                      acceptPolicyAgreement={acceptPolicyAgreement}
                      setAcceptPolicyAgreement={setAcceptPolicyAgreement}
                    >
                      <div className="social-btn-block">
                        {/*<ShopifyLoginBtn
                            btnText={`${isMobile ? '' : `${t('Sign up with')} `}Shopify`}
                            handleOk={handleAuthShopify}
                            fetching={fetching}
                          />*/}

                        <ButtonComponent
                          size="large"
                          onClick={responseGoogle}
                          block
                          disabled={!scriptLoadedSuccessfully || !acceptPolicyAgreement}
                        >
                          <img className="btn-icon icon-space" src={Images.google} alt="" />
                          {`${t("Sign up with")} `}Google
                        </ButtonComponent>
                      </div>
                      <div className="social-title">
                        <div className="middle-line" />
                        <span>{t("or")}</span>
                        <div className="middle-line" />
                      </div>
                    </SignUpEmailForm>

                    <div style={{ marginTop: "16px" }}>
                      <Text type="secondary">{t("Already have an account?")} </Text>
                      <Text type="link" className="text-link" onClick={() => navigate("/login")}>
                        {t("Sign in here")}
                      </Text>
                    </div>
                  </Card>
                </>
              )}
            </div>
            <PrivacyPolicy />
          </div>
          <div className="signup-split_right">
            <Carousel autoplay autoplaySpeed={5000} dots={false} className="signup-split_carousel">
              {signUpSlides.map((slide) => (
                <div key={slide.img} className="signup-split_slide">
                  <div className="signup-split_art">
                    <img src={`${process.env.PUBLIC_URL}/signup/${slide.img}`} alt="" />
                  </div>
                  <div className="signup-split_promo">
                    <h2 className="signup-split_promo-title">{t(slide.title)}</h2>
                    <Text className="signup-split_promo-text">{t(slide.subhead)}</Text>
                  </div>
                </div>
              ))}
            </Carousel>
          </div>
        </div>
      </Spin>
    </Layout>
  )
}

const mapStateToProps = (state) => ({
  auth: state.auth,
  isMobile: state.nav.isMobile,
  theme: state.nav.theme,
  inviteInfoFetching: state.setting.inviteInfoFetching,
  inviteInfo: state.setting.inviteInfo
})

// wraps dispatch to create nicer functions to call within our component
const mapDispatchToProps = (dispatch) => ({
  createAccount: (data, query, isReferralOutsider) =>
    dispatch(createAccount({ data, query, isReferralOutsider })),
  resetError: () => dispatch(authErrorReset()),
  socialAuth: (accessToken, type, user, isReferralOutsider) =>
    dispatch(socialAuth({ accessToken, socialType: type, user, isReferralOutsider })),
  getInviteUserInfo: (inviteId) => dispatch(getInviteUserInfo(inviteId))
  //shopifyAuth: (shopUrl) => dispatch(AuthActions.shopifyAuthRequest(shopUrl)),
})

export default connect(mapStateToProps, mapDispatchToProps)(SignUpPage)
